import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Lozenge from "@atlaskit/lozenge";
import Tooltip from "@atlaskit/tooltip";
import RetryIcon from "@atlaskit/icon/core/retry";
import { fetchFlowRunnerStatus } from "../redux/actions/integrationActions";
import { formatRunTimestamp, getStatusAppearance } from "./flow/status/flowStatusUtils";

export const FLOW_RUNNER_PENDING_EVENT = "ihub-flow-runner:pending-check";
const DEFAULT_PENDING_DURATION_MS = 30000;

const FlowRunner = () => {
  const dispatch = useDispatch();
  const { flowId } = useParams();
  const [isPolling, setIsPolling] = useState(false);
  const [lastAutoPollEnd, setLastAutoPollEnd] = useState(null);
  const isFetchingRef = useRef(false);
  const flowRunnerStatuses = useSelector((state) => state.integration.flowRunnerStatuses || {});
  const flow = useSelector((state) => state.integration.flow);
  const currentStatuses = flowId ? flowRunnerStatuses[flowId] : null;
  const pollSpinnerTimeoutRef = useRef(null);
  const nextPollTimeoutRef = useRef(null);
  const pendingStatusTimeoutRef = useRef(null);
  const [pendingStatus, setPendingStatus] = useState(null);

  const clearPendingStatus = useCallback(() => {
    if (pendingStatusTimeoutRef.current) {
      clearTimeout(pendingStatusTimeoutRef.current);
      pendingStatusTimeoutRef.current = null;
    }
    setPendingStatus(null);
  }, []);

  const fetchStatus = useCallback(
    async ({ trigger = "auto" } = {}) => {
      if (!flowId || isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      const isManual = trigger === "manual";
      let pollAnimationStart = null;
      if (!isManual) {
        if (nextPollTimeoutRef.current) {
          clearTimeout(nextPollTimeoutRef.current);
          nextPollTimeoutRef.current = null;
        }
        if (pollSpinnerTimeoutRef.current) {
          clearTimeout(pollSpinnerTimeoutRef.current);
          pollSpinnerTimeoutRef.current = null;
        }
        pollAnimationStart = Date.now();
        setIsPolling(true);
      }

      try {
        await dispatch(fetchFlowRunnerStatus(flowId));
      } finally {
        if (!isManual) {
          const stopAutoAnimation = () => {
            setIsPolling(false);
            setLastAutoPollEnd(Date.now());
          };
          const elapsed = pollAnimationStart ? Date.now() - pollAnimationStart : 0;
          const remaining = Math.max(5000 - elapsed, 0);
          if (remaining === 0) {
            stopAutoAnimation();
          } else {
            pollSpinnerTimeoutRef.current = setTimeout(() => {
              pollSpinnerTimeoutRef.current = null;
              stopAutoAnimation();
            }, remaining);
          }
        }
        isFetchingRef.current = false;
      }
    },
    [dispatch, flowId]
  );

  const schedulePendingStatus = useCallback(
    ({ status = "RUNNING", durationMs = DEFAULT_PENDING_DURATION_MS } = {}) => {
      clearPendingStatus();

      if (durationMs <= 0) {
        fetchStatus({ trigger: "manual" });
        return;
      }

      setPendingStatus({ status, expiresAt: Date.now() + durationMs });
      pendingStatusTimeoutRef.current = setTimeout(() => {
        pendingStatusTimeoutRef.current = null;
        setPendingStatus(null);
        fetchStatus({ trigger: "manual" });
      }, durationMs);
    },
    [clearPendingStatus, fetchStatus]
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (lastAutoPollEnd === null) {
      return undefined;
    }

    nextPollTimeoutRef.current = setTimeout(() => {
      fetchStatus({ trigger: "auto" });
    }, 30000);

    return () => {
      if (nextPollTimeoutRef.current) {
        clearTimeout(nextPollTimeoutRef.current);
        nextPollTimeoutRef.current = null;
      }
    };
  }, [lastAutoPollEnd, fetchStatus]);

  useEffect(() => {
    clearPendingStatus();
  }, [flowId, clearPendingStatus]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handlePendingRunEvent = (event) => {
      const detail = event?.detail;
      if (!detail || detail.flowId !== flowId) {
        return;
      }

      const durationMs = typeof detail.durationMs === "number" ? detail.durationMs : DEFAULT_PENDING_DURATION_MS;
      const status = detail.status || "RUNNING";

      schedulePendingStatus({ status, durationMs });
    };

    window.addEventListener(FLOW_RUNNER_PENDING_EVENT, handlePendingRunEvent);
    return () => {
      window.removeEventListener(FLOW_RUNNER_PENDING_EVENT, handlePendingRunEvent);
    };
  }, [flowId, schedulePendingStatus]);

  useEffect(() => {
    return () => {
      if (pollSpinnerTimeoutRef.current) {
        clearTimeout(pollSpinnerTimeoutRef.current);
      }
      if (nextPollTimeoutRef.current) {
        clearTimeout(nextPollTimeoutRef.current);
      }
      if (pendingStatusTimeoutRef.current) {
        clearTimeout(pendingStatusTimeoutRef.current);
      }
    };
  }, []);

  if (!flowId) {
    return null;
  }

  const isFlowDisabled = Boolean(flowId && flow?.id === flowId && flow.enabled === false);
  const latestStatus = Array.isArray(currentStatuses) ? currentStatuses[0] : null;
  const hasPendingStatus = Boolean(pendingStatus);
  const baseStatusLabel = hasPendingStatus
    ? pendingStatus.status
    : latestStatus
    ? latestStatus.status === "RUNNING"
      ? "RUNNING"
      : "IDLE"
    : "IDLE";
  const shouldShowPausedAppearance = isFlowDisabled && !hasPendingStatus && (!latestStatus || latestStatus.status !== "RUNNING");
  const statusLabel = shouldShowPausedAppearance ? "PAUSED" : baseStatusLabel;
  const appearanceStatus = hasPendingStatus ? pendingStatus.status : shouldShowPausedAppearance ? "PAUSED" : latestStatus?.status;
  const lozengeAppearance = getStatusAppearance(appearanceStatus);
  const tooltipText = hasPendingStatus
    ? "Run triggered via Test Flow. Checking status shortly..."
    : shouldShowPausedAppearance
    ? "Flow is disabled. Enable it to run."
    : latestStatus?.message ||
      latestStatus?.errorMessage ||
      (latestStatus ? `Started at ${formatRunTimestamp(latestStatus.startedAt)}` : "This flow has not been executed yet.");

  const isIconSpinning = isPolling || hasPendingStatus;

  return (
    <div
      className="ihub-flow-runner-status"
      style={{ marginInlineEnd: "100px", marginTop: "0px", display: "flex", alignItems: "center", gap: "0.5rem" }}
    >
      <Tooltip content={tooltipText}>
        <Lozenge appearance={lozengeAppearance} maxWidth={200}>
          {statusLabel}
        </Lozenge>
      </Tooltip>
      <span className={isIconSpinning ? "ihub-flow-runner-icon-spin" : undefined}>
        <RetryIcon label="Refresh status" />
      </span>
    </div>
  );
};

export default FlowRunner;
